import { useState, useEffect } from 'react';
import { X, Wheat } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { AgriCampaign, CropType } from '../../utils/agriEngine';
import { CROP_TYPE_LABELS } from '../../utils/agriEngine';

interface Props {
  campaign?: AgriCampaign | null;
  year: number;
  onClose: () => void;
  onSaved: () => void;
}

const defaultForm = {
  name: '',
  crop_type: 'ble' as CropType,
  surface_ha: '',
  target_yield_qtx_ha: '',
  target_price_eur_t: '',
  harvest_date: '',
  sowing_date: '',
  status: 'en_cours' as const,
  notes: '',
};

export function CampaignFormModal({ campaign, year, onClose, onSaved }: Props) {
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (campaign) {
      setForm({
        name: campaign.name,
        crop_type: campaign.crop_type,
        surface_ha: String(campaign.surface_ha),
        target_yield_qtx_ha: String(campaign.target_yield_qtx_ha),
        target_price_eur_t: String(campaign.target_price_eur_t),
        harvest_date: campaign.harvest_date || '',
        sowing_date: campaign.sowing_date || '',
        status: campaign.status,
        notes: campaign.notes || '',
      });
    }
  }, [campaign]);

  const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    if (!form.name || !form.surface_ha || !form.target_yield_qtx_ha || !form.target_price_eur_t) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSaving(true);
    setError('');
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }

    const payload = {
      user_id: user.id,
      name: form.name,
      crop_type: form.crop_type,
      surface_ha: parseFloat(form.surface_ha),
      target_yield_qtx_ha: parseFloat(form.target_yield_qtx_ha),
      target_price_eur_t: parseFloat(form.target_price_eur_t),
      harvest_date: form.harvest_date || null,
      sowing_date: form.sowing_date || null,
      campaign_year: year,
      status: form.status,
      notes: form.notes || null,
    };

    const { error: err } = campaign
      ? await supabase.from('agri_campaigns').update(payload).eq('id', campaign.id)
      : await supabase.from('agri_campaigns').insert(payload);

    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <Wheat className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {campaign ? 'Modifier la campagne' : 'Nouvelle campagne'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la campagne *</label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="Ex: Blé 2026" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Culture *</label>
              <select value={form.crop_type} onChange={e => set('crop_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm">
                {Object.entries(CROP_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm">
                <option value="en_cours">En cours</option>
                <option value="recolte">Récolté</option>
                <option value="cloturee">Clôturée</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Surface (ha) *</label>
              <input type="number" step="0.1" min="0" value={form.surface_ha} onChange={e => set('surface_ha', e.target.value)}
                placeholder="45" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rendement (qtx/ha) *</label>
              <input type="number" step="1" min="0" value={form.target_yield_qtx_ha} onChange={e => set('target_yield_qtx_ha', e.target.value)}
                placeholder="70" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix cible (€/t) *</label>
              <input type="number" step="1" min="0" value={form.target_price_eur_t} onChange={e => set('target_price_eur_t', e.target.value)}
                placeholder="220" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de semis</label>
              <input type="date" value={form.sowing_date} onChange={e => set('sowing_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de récolte</label>
              <input type="date" value={form.harvest_date} onChange={e => set('harvest_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm resize-none" />
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm transition-colors">
            Annuler
          </button>
          <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm transition-colors disabled:opacity-50">
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
